import type { ITileManagerModel } from '@/interfaces/models';
import { fromIndex, toIndex, isValidTilePosition } from '@/models/TileManagerModel';

const cache = new WeakMap<ITileManagerModel, { version: number; contiguous: boolean }>();

/** Edge-adjacent tiles form one structure; cached until its topology changes. */
export function isStructureContiguous(manager: ITileManagerModel): boolean {
  const version = manager.structureVersion;
  const cached = cache.get(manager);
  if (version !== undefined && cached?.version === version) return cached.contiguous;
  const tiles = manager.tiles;
  if (tiles.length < 2) return true;
  const positions = new Set(tiles.map(tile => toIndex(tile.location.x, tile.location.y)));
  const visited = new Set<number>();
  const start = toIndex(tiles[0].location.x, tiles[0].location.y);
  const queue = [start];
  visited.add(start);
  for (let head = 0; head < queue.length; head++) {
    const [x, y] = fromIndex(queue[head]);
    for (const [nx, ny] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]]) {
      if (!isValidTilePosition(nx, ny)) continue;
      const index = toIndex(nx, ny);
      if (positions.has(index) && !visited.has(index)) {
        visited.add(index);
        queue.push(index);
      }
    }
  }
  const contiguous = visited.size === tiles.length;
  if (version !== undefined) cache.set(manager, { version, contiguous });
  return contiguous;
}
